/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import { TicketType } from '@touchplan/common';
import { ExportFormatType } from '../modules/reports/types/ExportFormatType';
import { ExternalLinkage } from '../modules/reports/types/ExternalLinkage';
import { ICustomFieldFilter, ITPFilter } from '../app-data/models/TPFilterPredicates';
import { GroupBy } from '../modules/reports/types/GroupBy';

// the types added to this enum should be the names of requests that fbdb recognizes
export enum RequestType {
	projectReport = 'projectReport',
	projectReportBundle = 'projectReportBundle',
	projectCopy = 'projectCopy',
	planCopy = 'planCopy',
	biembedUrl = 'biEmbedUrl',
	intercomInviteNewUser = 'inviteNewUser',
	intercomInviteExistingUser = 'inviteExistingUser',
	planTables = 'planTables',
	projectTables = 'projectTables',
	ticketExport = 'ticketExport',
	planNearline = 'planNearline',
	takeSnapshot = 'snapshot',
}

export interface IFbdbRequestResult {
	requestId: string;
	completedAt?: string;
	startedAt: string;
	startedBy: string;
	startedIn: string;
	statusAt?: string;
	statusMax?: number;
	statusMessage?: string;
	statusStep?: number;
	errorMessage?: string;
}

export interface IFbdbRequest {
	name: string;
	params: any;
	requestingUserId: string;
	results?: IFbdbRequestResult;
}

export interface FbdbRequestConfig {
	disableDeletion?: boolean;
	includeRequest?: boolean;
	overrideRequestId?: string;
	//can add a userId override if necessary
	// Min is 30 seconds - lower values will be ignored
	timeoutOverride?: number;
}

export interface ProjectCopyConfig {
	srcProjectId: string;
	ownerUserId: string;
}
export interface IProjectCopyResult extends IFbdbRequestResult {
	destProjectId?: string;
}

export interface PlanCopyConfig {
	srcProjectId: string;
	srcPlanId: string;
	nameFormatPattern?: string;
	numberOfCopies?: number;
}

export interface ITakeSnapshotConfig {
	projectId: string;
	planId: string;
	snapshot: Record<string, string>;
}
export interface ITakeSnapshotResult extends IFbdbRequestResult {
	snapshotId: string;
}

export interface IPlanCopyResult extends IFbdbRequestResult {
	destPlanIds?: string[];
	destPlanNames?: string[];
}

export interface DefaultProjectCopyConfig {
	ownerUserId: string;
}

export interface IBiembedUrlConfig {
	biEmbedId: string;
	biEmbedMemberId: string;
	embed_domain: string;
}

export interface IBiIndependentEmbedUrlConfig {
	biEmbedId: string;
	userId: string;
	embed_domain: string;
	lookerParams: object;
}

export interface IBiembedUrlResult extends IFbdbRequestResult {
	biEmbedUrl: string;
}

interface IntercomInviteGenericUserConfig {
	projectId: string;
	inviterUserId: string;
	inviterMessage?: string;
}

export interface IntercomInviteNewUserConfig extends IntercomInviteGenericUserConfig {
	inviteeEmail: string;
}
export interface IntercomInviteNewUserResult extends IFbdbRequestResult {
	mailgunId: string;
	mailgunMessage: string;
}

export interface IntercomInviteExistingUserConfig extends IntercomInviteGenericUserConfig {
	inviteeIntercomId: string;
}

export interface IFbdbRequestPlanProjectResults extends IFbdbRequestResult {
	requestId: string;
	//...one of these sets is legacy, probably
	csvBlob?: string;
	csvBucket?: string;
	gcsBlob?: string;
	gcsBucket?: string;
}

export interface ICsvPlanConfig {
	projectId: string;
	planId: string;
}

export interface ICsvProjectConfig {
	projectId: string;
	includeArchivedPlans?: boolean;
}

export interface IProjectReportsConfig {
	projectId: string;
	reportName: string;
	reportTitle: string;
	granularity?: string;
	asOfDate?: string;
	upToDate?: string;
	roleIds?: string[];
	planIds?: string[];
	locationIds?: string[];
	swimlaneIds?: string[];
	scale?: number;
	includeNoLocation?: boolean;
	includeNoSwimlane?: boolean;
	includeCompleted?: boolean;
	groupBy?: GroupBy;
	orderGroupByName?: boolean;
	groupByLocation?: boolean;
	orderLocationByName?: boolean;
	orderByDate?: boolean;
	onlyLateConstraints?: boolean;
	customFieldFilters?: ICustomFieldFilter[];
	includePullTickets?: boolean;
	ticketTypes?: TicketType[];
	externalLinkage?: ExternalLinkage;
	exportTicketsFormat?: ExportFormatType;
	gcsBucket?: string;
	gcsBlob?: string;
	customFieldIds?: string[];
	wbsCodeFilter?: ITPFilter;
	wbsGroupingLevel?: number;
	archivedPlanIds?: string[];
}

export type INonExportTickets = Omit<
	IProjectReportsConfig,
	'includePullTickets' | 'ticketTypes' | 'externalLinkage' | 'exportTicketsFormat' | 'gcsBucket' | 'gcsBlob'
>;

// These commented-out interfaces aren't used anywhere,
// and they're not necessary or helpful in defining the IAnyReportTypes interface
// defined below:
//
// export type IConstraintLog = Omit<
// 	INonExportTickets,
// 	'planIds' | 'locationIds' | 'asOfDate' | 'upToDate' | 'granularity'
// >;
// export type IDraftTickets = Omit<INonExportTickets, 'granularity'>;
// export type IExportToCSV = Omit<INonExportTickets, 'granularity'>;
// export type IGanttChart = Omit<INonExportTickets, 'onlyLateConstraints'>;
// export type IOverdueActivity = Omit<INonExportTickets, 'onlyLateConstraints'>;
export type IPPCReport = Omit<INonExportTickets, 'granularity'>;
// export type ISixWeekLookAhead = Omit<INonExportTickets, 'onlyLateConstraints' | 'granularity'>;
// export type IVarianceReason = Omit<INonExportTickets, 'onlyLateConstraints' | 'granularity'>;
export type IWeeklyWorkPlan = Omit<INonExportTickets, 'onlyLateConstraints' | 'granularity'>;
export type IExportTicketsConfig = Omit<IProjectReportsConfig, 'onlyLateConstraints' | 'granularity'>;

export type IAnyReportTypes = IProjectReportsConfig;
// | IConstraintLog
// | IGanttChart
// | IPPCReport
// | IWeeklyWorkPlan
// | ISixWeekLookAhead
// | IDraftTickets
// | IOverdueActivity
// | IVarianceReason
// | IExportTicketsConfig;

export interface IProjectBundleReportsConfig extends IProjectReportsConfig {
	bundleIndex: number;
	bundleReports: string[];
}

export interface IProjectReportsFbdbRequestResults extends IFbdbRequestResult {
	reportBodyBlob?: string;
	reportBucket?: string;
	reportName: string;
}

export interface IBundleHeading {
	projectId: string;
	reportName: string;
}

export interface IPlanNearlineRequestResults extends IFbdbRequestResult {
	planIds: string[];
}
